/* mes-size-guide.js — the PDP size guide as a SELF-INITIALIZING custom element (17 Jul 2026).

   Was an inline <script> that used document.currentScript to scope itself. That never ran when
   the size guide was loaded inside the QUICK-BUY drawer — the quick-buy fetches its content and
   injects it as HTML, and injected <script> tags do not execute. So in the quick-buy (from the
   cart OR from a PDP's recommended-products) the setup never ran and the guide rendered in its
   raw, no-JS state: fully open with the size chart AND the "how to measure" diagram stacked
   (Shrey's screenshot). Bug was present on live too (the size-guide snippet is identical there).

   As a custom element it upgrades and runs connectedCallback wherever it appears — PDP,
   quick-buy, cart — with no reliance on inline script execution. The body below is IDENTICAL to
   the old inline script; only the root acquisition changed (document.currentScript.closest ->
   this.closest). Defined once, globally (loaded in layout/theme.liquid), so any <mes-size-guide>
   injected later auto-upgrades. */
if (!(window.customElements && window.customElements.get('mes-size-guide'))) {
  window.customElements.define('mes-size-guide', class extends HTMLElement {
    connectedCallback() {
      var option = this.closest('.variant-picker__option');
      if(!option || option.dataset.mesSgBound){ return; }
      option.dataset.mesSgBound = '1';
      var sg = option.querySelector('[data-mes-sg]');
      if(!sg){ return; }
      var flipWrap = sg.querySelector('[data-mes-sg-flip-wrap]');
      var inner = sg.querySelector('[data-mes-sg-inner]');
      var front = sg.querySelector('[data-mes-sg-front]');
      var back = sg.querySelector('[data-mes-sg-back]');
      var link = option.querySelector('[data-mes-sg-toggle]');
      var sizeValues = option.querySelector('.variant-picker__option-values');

      /* V9.28.49: the kada guide keeps the size buttons visible (its numbers live under them). */
      var keepSizes = !!sg.querySelector('[data-mes-sg2]');
      if(keepSizes){ option.classList.add('mes-sg-keepsizes'); }

      var closedHeight = 0;
      function measureClosed(){
        if(!sizeValues || option.classList.contains('mes-sg-open')){ return; }
        var prev = sizeValues.style.maxHeight;
        sizeValues.style.maxHeight = 'none';
        closedHeight = sizeValues.scrollHeight;
        sizeValues.style.maxHeight = prev;
      }
      function applySizeValues(){
        if(!sizeValues){ return; }
        if(keepSizes){
          /* V9.28.49e: the chart face lets you pick a size from the table, so the buttons step aside there. */
          var onChart = option.classList.contains('mes-sg-open') && flipWrap.classList.contains('mes-sg-flipped');
          sizeValues.style.maxHeight = onChart ? '0px' : 'none';
          return;
        }
        sizeValues.style.maxHeight = option.classList.contains('mes-sg-open') ? '0px' : (closedHeight + 'px');
      }
      function fitSizeValues(){ measureClosed(); applySizeValues(); }

      function setHeight(face){ if(inner && face){ inner.style.height = face.scrollHeight + 'px'; } }

      function show(face){ setHeight(face); sg.style.maxHeight = face.scrollHeight + 'px'; }
      function toggleGuide(){
        var open = option.classList.toggle('mes-sg-open');
        flipWrap.classList.remove('mes-sg-flipped');
        if(link){ link.setAttribute('aria-expanded', open ? 'true' : 'false'); }
        if(open){ filterRows(); show(front); }
        else { sg.style.maxHeight = '0px'; }
        applySizeValues();
        if(!open){ setTimeout(function(){ measureClosed(); applySizeValues(); }, 480); }
      }
      function flip(){
        var flipped = flipWrap.classList.toggle('mes-sg-flipped');
        show(flipped ? back : front);
        applySizeValues();
      }
      function syncActive(){
        var checked = option.querySelector('input:checked');
        var val = checked ? (checked.value || '').trim().toUpperCase() : '';
        sg.querySelectorAll('[data-sg-size]').forEach(function(r){
          r.classList.toggle('is-active', r.getAttribute('data-sg-size').toUpperCase() === val);
        });
      }

      function normSize(s){ return (s || '').trim().toUpperCase(); }
      function swatchFor(size){
        var btns = Array.prototype.slice.call(option.querySelectorAll('.block-swatch'));
        var want = normSize(size);
        /* V9.28.49e: match on the radio's VALUE first - a kada button also carries its cm caption,
           so matching the label's text would miss ("L 9.4 cm" !== "L"). */
        var byValue = btns.filter(function(b){
          var input = b.previousElementSibling;
          return input && input.tagName === 'INPUT' && normSize(input.value) === want;
        })[0];
        if(byValue){ return byValue; }
        return btns.filter(function(b){ return normSize(b.textContent) === want; })[0] || null;
      }
      function swatchUsable(sw){ return !!sw && !sw.classList.contains('is-disabled'); }
      function filterRows(){
        var rows = Array.prototype.slice.call(sg.querySelectorAll('[data-sg-size]'));
        if(!rows.length){ return; }
        var pairs = rows.map(function(r){ return { row: r, sw: swatchFor(r.getAttribute('data-sg-size')) }; });
        var anyMatched = pairs.some(function(p){ return !!p.sw; });
        /* V9.28.52: on a fully sold-out product every row used to hide, leaving an EMPTY chart.
           If nothing is buyable, show the whole chart - the sizes are still the sizes. */
        var anyUsable = pairs.some(function(p){ return swatchUsable(p.sw); });
        pairs.forEach(function(p){
          p.row.classList.toggle('mes-sg-row--hidden', anyMatched && anyUsable && !swatchUsable(p.sw));
        });
      }
      function reflowGuide(){
        if(option.classList.contains('mes-sg-open')){
          show(flipWrap.classList.contains('mes-sg-flipped') ? back : front);
        }
      }

      if(link){ link.addEventListener('click', function(e){ e.preventDefault(); toggleGuide(); }); }
      sg.querySelectorAll('[data-mes-sg-flip]').forEach(function(b){ b.addEventListener('click', flip); });
      sg.querySelectorAll('[data-sg-size]').forEach(function(row){
        row.addEventListener('click', function(){
          var match = swatchFor(row.getAttribute('data-sg-size'));
          if(swatchUsable(match)){
            var input = match.previousElementSibling;
            if(input && input.tagName === 'INPUT'){ input.click(); } else { match.click(); }
          }
          syncActive();
        });
      });
      var _syncT = null;
      function scheduleSync(){ if(_syncT){ clearTimeout(_syncT); } _syncT = setTimeout(function(){ _syncT = null; syncActive(); filterRows(); reflowGuide(); }, 0); }
      option.addEventListener('change', function(){ scheduleSync(); fitSizeValues(); });
      document.addEventListener('variant:change', scheduleSync);
      syncActive();
      fitSizeValues();
      sg.addEventListener('mes-pdp3:size-required', function(){
        if(!option.classList.contains('mes-sg-open')){ toggleGuide(); }
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        option.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        option.classList.remove('mes-sg-flash');
        void option.offsetWidth;
        option.classList.add('mes-sg-flash');
        setTimeout(function(){ option.classList.remove('mes-sg-flash'); }, 1600);
      });
      var rT; window.addEventListener('resize', function(){ clearTimeout(rT); rT = setTimeout(function(){ fitSizeValues(); if(option.classList.contains('mes-sg-open')){ show(flipWrap.classList.contains('mes-sg-flipped') ? back : front); } }, 150); });
    }
  });
}
